import React from 'react';
import { ConnectivityBanner, useOnlineStatus } from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { BFF_URL } from '../../lib/runtimeEnv.js';
import { isVisitPath } from '../../constants/routes.js';

// The app-wide «you are not connected» bar. core.md §7.9 / §15.
//
// Mounted ONCE per shell (the desktop rail's <main> and the mobile shell's),
// above whatever screen is routed, so the list, the record, the profile and
// the credits page all say the same thing when the network goes — rather than
// each panel blaming itself with its own error card. The kit's banner carries
// no catalogue (its defaults are Persian literals), so every label comes from
// here; the probe URL is this app's own BFF.
//
// `useOnlineStatus` returns an OBJECT. A truthiness check on it is always
// true; the fields are what carry the answer.
//
// ── Skipped on the visit route ────────────────────────────────────────────
//
// The visit workspace renders its own offline notice (`visit.offline.*`),
// with the sentence that matters THERE: the SOAP draft is kept on the device
// and sent when the connection returns. Two bars saying "offline" over one
// screen would be noise, and the visit's copy is the more useful of the two,
// so this one steps aside for it. The chat page keeps this bar: its composer
// only disables itself with «آفلاین», which is a control state, not a notice.

export default function AppConnectivityBanner({ pathname }) {
  const { t } = useTranslation();
  const online = useOnlineStatus({ url: BFF_URL });
  const [checking, setChecking] = React.useState(false);

  const retry = React.useCallback(async () => {
    setChecking(true);
    try {
      await online.check();
    } finally {
      setChecking(false);
    }
  }, [online]);

  if (isVisitPath(pathname)) return null;

  return (
    <ConnectivityBanner
      state={online.state}
      recovered={online.recovered}
      onAcknowledgeRecovery={online.acknowledgeRecovery}
      onRetry={retry}
      busy={checking}
      offlineLabel={t('connectivity.offline')}
      degradedLabel={t('connectivity.degraded')}
      restoredLabel={t('connectivity.restored')}
      retryLabel={t('connectivity.retry')}
      busyLabel={t('connectivity.checking')}
    />
  );
}
